import { LooplaySDKError } from '../../errors';
import { ServiceClient, type ServiceClientOptions } from '../../apps/service-client';
import { decodeJwtExpMs } from '../jwt';
import type {
  AuthLoginParams,
  AuthLoginResult,
  AuthProvider,
  LooplaySession,
  LooplayUser
} from '../types';

export interface TelegramAuthLoginParams extends AuthLoginParams {
  telegramInitData?: string;
}

export interface TelegramAuthProviderOptions extends ServiceClientOptions {
  /**
   * Optional override for how to read Telegram init data.
   * If omitted, provider will try `params.telegramInitData` then `globalThis.Telegram.WebApp.initData`.
   */
  getTelegramInitData?: () => string | undefined;

  /**
   * Optional mapper from backend user payload to LooplayUser.
   * If omitted, provider will try common fields like id/_id/userId/telegramId.
   */
  mapUser?: (rawUser: Record<string, any>) => LooplayUser;
}

class MissingTelegramInitDataError extends LooplaySDKError {
  constructor() {
    super('Missing Telegram init data');
    this.name = 'MissingTelegramInitDataError';
  }
}

class MissingRefreshTokenError extends LooplaySDKError {
  constructor() {
    super('Missing refresh token');
    this.name = 'MissingRefreshTokenError';
  }
}

export class TelegramAuthProvider implements AuthProvider {
  readonly id = 'telegram';

  private readonly client: ServiceClient;
  private readonly getTelegramInitData?: () => string | undefined;
  private readonly mapUser?: (rawUser: Record<string, any>) => LooplayUser;

  private session: LooplaySession | null = null;

  constructor(options: TelegramAuthProviderOptions) {
    this.client = new ServiceClient(options);
    this.getTelegramInitData = options.getTelegramInitData;
    this.mapUser = options.mapUser;
  }

  onSessionChanged = (session: LooplaySession | null): void => {
    this.session = session;
  };

  async login(params?: TelegramAuthLoginParams): Promise<AuthLoginResult> {
    const initData =
      params?.telegramInitData ??
      this.getTelegramInitData?.() ??
      (globalThis as any)?.Telegram?.WebApp?.initData;

    if (!initData) throw new MissingTelegramInitDataError();

    const result = await this.client.telegramLogin(initData);

    const rawUser = (result.user ?? {}) as Record<string, any>;
    const user = this.mapUser ? this.mapUser(rawUser) : this.defaultMapUser(rawUser);

    const session: LooplaySession = {
      user,
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: decodeJwtExpMs(result.accessToken)
    };

    this.session = session;
    return { session };
  }

  async refresh(): Promise<Pick<LooplaySession, 'accessToken' | 'refreshToken' | 'expiresAt'>> {
    const currentSession = this.session;
    const refreshToken = currentSession?.refreshToken;
    if (!currentSession || !refreshToken) throw new MissingRefreshTokenError();

    const result = await this.client.refresh(refreshToken);

    const next = {
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: decodeJwtExpMs(result.accessToken)
    } satisfies Pick<LooplaySession, 'accessToken' | 'refreshToken' | 'expiresAt'>;

    this.session = { ...currentSession, ...next };
    return next;
  }

  async logout(): Promise<void> {
    const refreshToken = this.session?.refreshToken;

    if (refreshToken) {
      try {
        await this.client.logout(refreshToken);
      } catch {
        // best-effort
      }
    }

    this.session = null;
  }

  private defaultMapUser(raw: Record<string, any>): LooplayUser {
    const id = raw.id ?? raw._id ?? raw.userId ?? raw.telegramId;
    if (id === undefined || id === null || String(id).trim() === '') {
      throw new LooplaySDKError('Missing user id from auth response');
    }

    return {
      id: String(id),
      displayName: raw.displayName ?? raw.username ?? raw.name,
      avatarUrl: raw.avatarUrl ?? raw.photoUrl ?? raw.avatar
    };
  }
}
